import React, { useState, useEffect, useRef } from 'react';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Bookmark, Search, X, Clock, Pencil, Variable, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import FUQManageDialog from './FUQManageDialog';
import VariableForm from './VariableForm';
import { hasVariables } from './variableEngine';

/* ── Helpers ── */
function daysSince(isoString) {
  if (!isoString) return null;
  return Math.floor((Date.now() - new Date(isoString).getTime()) / 86400000);
}

function recencyDotClass(isoString) {
  const d = daysSince(isoString);
  if (d === null) return 'bg-slate-600';
  if (d < 7)  return 'bg-emerald-400';
  if (d < 30) return 'bg-amber-400';
  return 'bg-slate-500';
}

/* Build a map of fuq_id → { d7, d30, d90 } counts from raw events */
function buildCountMap(events) {
  const now = Date.now();
  const map = {};
  for (const ev of events) {
    const id = ev.fuq_id;
    if (!id) continue;
    if (!map[id]) map[id] = { d7: 0, d30: 0, d90: 0 };
    const age = Math.floor((now - new Date(ev.run_at).getTime()) / 86400000);
    if (age <= 7)  map[id].d7++;
    if (age <= 30) map[id].d30++;
    if (age <= 90) map[id].d90++;
  }
  return map;
}

/* Velocity: compare 7d rate vs 30d rate (normalised to per-week).
   Returns: 'up' | 'flat' | 'down' */
function velocity(counts) {
  if (!counts) return 'flat';
  const rate7d  = counts.d7;
  const rate30d = counts.d30 / 4; /* avg week over last 30d */
  if (rate30d === 0 && rate7d === 0) return 'flat';
  if (rate30d === 0) return 'up';
  const ratio = rate7d / rate30d;
  if (ratio >= 1.5) return 'up';
  if (ratio <= 0.5) return 'down';
  return 'flat';
}

function TrendIcon({ v }) {
  if (v === 'up')   return <TrendingUp   className="w-3 h-3 text-emerald-400 flex-shrink-0" />;
  if (v === 'down') return <TrendingDown className="w-3 h-3 text-slate-500 flex-shrink-0" />;
  return <Minus className="w-3 h-3 text-slate-600 flex-shrink-0" />;
}

/* windowCount: how many runs in selected window */
function windowCount(counts, windowDays) {
  if (!counts) return 0;
  if (windowDays <= 7)  return counts.d7;
  if (windowDays <= 30) return counts.d30;
  return counts.d90;
}

/* Sort: by windowed count desc, then by all-time count desc */
function sortFuqs(fuqs, countMap, windowDays) {
  return fuqs.slice().sort((a, b) => {
    const wA = windowDays === Infinity ? (a.usage_count || 0) : windowCount(countMap[a.id], windowDays);
    const wB = windowDays === Infinity ? (b.usage_count || 0) : windowCount(countMap[b.id], windowDays);
    if (wB !== wA) return wB - wA;
    return (b.usage_count || 0) - (a.usage_count || 0);
  });
}

/* Write a usage event — fire-and-forget, never throws */
async function recordUsageEvent(fuqId, fuqName) {
  try {
    const user = await base44.auth.me();
    await base44.entities.FUQUsageEvent.create({
      fuq_id: fuqId,
      fuq_name: fuqName || '',
      run_at: new Date().toISOString(),
      user_email: user?.email || ''
    });
  } catch (e) { /* non-critical */ }
}

const WINDOW_OPTIONS = [
  { label: '7d',  days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: 'All', days: Infinity },
];

export default function FUQChips({ onSelectQuery, canEdit = false }) {
  const [fuqs, setFuqs] = useState([]);
  const [countMap, setCountMap] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const dropdownTriggerRef = useRef(null);
  const [dropUp, setDropUp] = useState(false);
  const [editFuq, setEditFuq] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingFuq, setPendingFuq] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    /* Load FUQs and last-90-days events in parallel */
    const cutoff = new Date(Date.now() - 90 * 86400000).toISOString();
    const [allFuqs, events] = await Promise.all([
      base44.entities.FrequentlyUsedQuery.list('-created_date', 500),
      base44.entities.FUQUsageEvent.filter(
        { run_at: { $gte: cutoff } },
        '-run_at',
        2000
      ).catch(() => [])  /* graceful if entity is empty */
    ]);
    setFuqs(allFuqs);
    setCountMap(buildCountMap(events));
  };

  /* Top chips: 3 most-run FUQs in the last 30d */
  const topFuqs = sortFuqs(fuqs, countMap, 30).slice(0, 3);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.toLowerCase();
    setSearchResults(fuqs.filter(f =>
      f.name?.toLowerCase().includes(q) ||
      f.description?.toLowerCase().includes(q) ||
      f.tags?.some(t => t.toLowerCase().includes(q))
    ));
  }, [searchQuery, fuqs]);

  const handleSelect = async (fuq) => {
    if (hasVariables(fuq.query)) {
      let defs = [];
      try { defs = fuq.variable_definitions ? JSON.parse(fuq.variable_definitions) : []; }
      catch (e) { defs = []; }
      setPendingFuq({ ...fuq, parsedDefs: Array.isArray(defs) ? defs : [] });
      setShowSearch(false); setSearchQuery('');
      return;
    }
    onSelectQuery(fuq.query);
    /* Update both cumulative counter and event log */
    await Promise.all([
      base44.entities.FrequentlyUsedQuery.update(fuq.id, {
        usage_count: (fuq.usage_count || 0) + 1,
        last_used_at: new Date().toISOString()
      }),
      recordUsageEvent(fuq.id, fuq.name)
    ]);
    setShowSearch(false); setSearchQuery('');
    loadData();
  };

  const handleVariableExecute = async (resolvedSQL) => {
    if (!pendingFuq) return;
    onSelectQuery(resolvedSQL);
    await Promise.all([
      base44.entities.FrequentlyUsedQuery.update(pendingFuq.id, {
        usage_count: (pendingFuq.usage_count || 0) + 1,
        last_used_at: new Date().toISOString()
      }).catch(() => {}),
      recordUsageEvent(pendingFuq.id, pendingFuq.name)
    ]);
    setPendingFuq(null);
    loadData();
  };

  if (fuqs.length === 0 && !pendingFuq) return null;

  const baseList = searchQuery.trim() ? searchResults : fuqs;
  const dropdownList = sortFuqs(baseList, countMap, windowDays);

  return (
    <div className="space-y-2 mb-3">
      <div className="flex flex-wrap items-center gap-2">
        <Bookmark className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mr-1">FUQ</span>

        {topFuqs.map(fuq => {
          const counts = countMap[fuq.id];
          const c30 = counts?.d30 || 0;
          const v = velocity(counts);
          return (
            <button
              key={fuq.id}
              onClick={() => handleSelect(fuq)}
              title={fuq.description || fuq.query}
              className={`px-3 py-1 rounded-full text-xs border transition-all truncate max-w-[200px] flex items-center gap-1 ${
                pendingFuq?.id === fuq.id
                  ? 'bg-amber-200 text-amber-900 border-amber-400'
                  : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 hover:border-amber-400'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${recencyDotClass(fuq.last_used_at)}`}
                title={`${c30} runs in last 30d`}
              />
              {fuq.name}
              {hasVariables(fuq.query) && <Variable className="w-3 h-3 text-amber-500/60 flex-shrink-0" />}
            </button>
          );
        })}

        {/* Search chip */}
        <div className="relative">
          <button
            ref={dropdownTriggerRef}
            onClick={() => {
              if (!showSearch && dropdownTriggerRef.current) {
                const rect = dropdownTriggerRef.current.getBoundingClientRect();
                setDropUp(window.innerHeight - rect.bottom < 320);
              }
              setShowSearch(!showSearch);
              setSearchQuery('');
            }}
            className="px-3 py-1 rounded-full text-xs border border-dashed border-slate-600 text-slate-400 hover:border-amber-400 hover:text-amber-300 transition-all flex items-center gap-1"
          >
            <Search className="w-3 h-3" />
            Search FUQs
          </button>

          {showSearch && (
            <div className={`absolute left-0 w-80 bg-slate-800 border border-slate-700 rounded-lg shadow-2xl z-50 p-3 space-y-2 ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}>

              {/* Search row */}
              <div className="flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                <Input data-testid="sentinel-fuqchips-input"
                  ref={searchRef}
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, description, or tag..."
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 h-8 text-xs"
                />
                <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="text-slate-500 hover:text-slate-300 flex-shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Time window selector */}
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-slate-500 flex-shrink-0" />
                <span className="text-[10px] text-slate-500 mr-1">Show runs in last:</span>
                {WINDOW_OPTIONS.map(opt => (
                  <button
                    key={opt.label}
                    onClick={() => setWindowDays(opt.days)}
                    data-testid={`fuq-window-${opt.label}`}
                    className={`px-1.5 py-0.5 rounded text-[10px] transition-colors ${
                      windowDays === opt.days
                        ? 'bg-amber-500 text-slate-900 font-semibold'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* FUQ list */}
              <div className="max-h-[220px] overflow-y-auto space-y-1">
                {dropdownList.map(fuq => {
                  const counts = countMap[fuq.id];
                  const wCount = windowDays === Infinity
                    ? (fuq.usage_count || 0)
                    : windowCount(counts, windowDays);
                  const v = velocity(counts);

                  return (
                    <div key={fuq.id} className="flex items-center gap-1 group px-2 py-1.5 rounded-md hover:bg-slate-700/50 transition-colors">
                      {canEdit && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditFuq(fuq); setEditOpen(true); setShowSearch(false); }}
                          className="p-1 rounded-md text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-all flex-shrink-0"
                          title="Edit FUQ"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => handleSelect(fuq)}
                        className="flex-1 text-left flex items-center justify-between min-w-0"
                      >
                        <span className="text-sm text-slate-200 group-hover:text-amber-300 truncate flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${recencyDotClass(fuq.last_used_at)}`} />
                          {fuq.name}
                          {hasVariables(fuq.query) && <Variable className="w-3 h-3 text-amber-400/50 flex-shrink-0" />}
                        </span>

                        {/* Count + trend */}
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <TrendIcon v={v} />
                          {wCount > 0 ? (
                            <Badge className="bg-amber-500/20 text-amber-400 border-none text-[10px] leading-tight">
                              {wCount}
                              {windowDays !== Infinity && (
                                <span className="ml-0.5 text-amber-600/70">
                                  /{windowDays}d
                                </span>
                              )}
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-slate-600">
                              {windowDays === Infinity ? '0' : `0/${windowDays}d`}
                            </span>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
                {searchQuery.trim() && searchResults.length === 0 && (
                  <div className="text-xs text-slate-500 text-center py-3">No FUQs found</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {pendingFuq && (
        <VariableForm
          sqlTemplate={pendingFuq.query}
          definitions={pendingFuq.parsedDefs}
          onExecute={handleVariableExecute}
          onCancel={() => setPendingFuq(null)}
        />
      )}

      <FUQManageDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        fuq={editFuq}
        onSaved={loadData}
        onDeleted={loadData}
      />
    </div>
  );
}
